import { FormattedMessage } from 'react-intl';

import { Box, Grid, Link, Typography } from '@mui/material';

import { useStrOfferingData } from '@arrived/common';
import { CONFIG } from '@arrived/config';
import { FEATURE_FLAGS, useIsFeatureFlagEnabled } from '@arrived/feature-flags';
import { AssetType, Market, Offering, Property } from '@arrived/models';

import { PROPERTY_HASH_MAP } from '../Property.constants';
import { PropertyDetailsSection } from './PropertyDetailsSection';
import { PropertyDetailsTextSection } from './PropertyDetailsTextSection';
import { PropertyLocationMap } from './PropertyLocationMap';

interface PropertyDetailsMarketInfoProps {
  market: Market | undefined;
  property: Property;
  offering: Offering;
}

export const PropertyDetailsMarketInfoLTR = ({ market, property }: PropertyDetailsMarketInfoProps) => {
  const { title, description } = market || ({} as Market);

  if (!title || !description) return null;

  return (
    <PropertyDetailsSection
      hash={PROPERTY_HASH_MAP.market}
      title={<FormattedMessage id="property-details-market-title" defaultMessage="The Market" />}
      description={
        <FormattedMessage
          id="property-details-market-description"
          defaultMessage="Real estate values and returns are highly dependent on location. In general, properties in more affordable markets will have higher cash flow (potential dividends), and properties in more expensive markets will have higher appreciation. Arrived strives to give investors options to choose how much they invest in appreciation markets, cash flow markets, or balanced markets.{br}{br}The economy of the local city and market will dictate the potential returns of an investment. In general, some markets see high appreciation of home values, some have higher cash flow (dividends), and some have a mix of the two."
          values={{ br: <br /> }}
        />
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={5}>
          <PropertyDetailsTextSection
            text={
              <FormattedMessage
                id="property-details-market-description-text"
                defaultMessage={description}
                values={{ br: <br /> }}
              />
            }
            title={title}
          />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={7}>
          <PropertyLocationMap property={property} />
        </Grid>
      </Grid>
    </PropertyDetailsSection>
  );
};

export const PropertyDetailsMarketInfoSTR = ({ market, property, offering }: PropertyDetailsMarketInfoProps) => {
  const { title } = market || ({} as Market);
  const { market: marketSTR } = useStrOfferingData(offering.id);
  const airDnaMarketGradeEnabled = useIsFeatureFlagEnabled(FEATURE_FLAGS.AIR_DNA_MARKET_GRADE);

  const description = marketSTR.description(({ href, text }) => (
    <Link href={href} target="_blank" underline="always">
      {text}
    </Link>
  ));

  return (
    <PropertyDetailsSection
      hash={PROPERTY_HASH_MAP.market}
      title={<FormattedMessage id="property-details-market-title" defaultMessage="The Market" />}
      description={
        <FormattedMessage
          id="property-details-market-description"
          defaultMessage="Real estate values and returns are highly dependent on location. In general, properties in more affordable markets will have higher cash flow (potential dividends), and properties in more expensive markets will have higher appreciation. Arrived strives to give investors options to choose how much they invest in appreciation markets, cash flow markets, or balanced markets.{br}{br}The economy of the local city and market will dictate the potential returns of an investment. In general, some markets see high appreciation of home values, some have higher cash flow (dividends), and some have a mix of the two."
          values={{ br: <br /> }}
        />
      }
    >
      <Grid container spacing={3}>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={5}>
          <PropertyDetailsTextSection text={description} title={title} />
        </Grid>
        <Grid item xs={12} sm={12} md={6} lg={6} xl={7}>
          <PropertyLocationMap property={property} />
        </Grid>
        {airDnaMarketGradeEnabled && (
          <Grid item xs={12}>
            <Typography variant="h4.semibold">
              <FormattedMessage id="property-details-market-grade-title" defaultMessage="Market Grade" />
            </Typography>
            <Box width="100%" display="flex" flexDirection="column" alignItems="center" justifyContent="center">
              <img
                src={marketSTR.marketGradeImageSrc}
                alt="AirDNA market grade report"
                width="100%"
                style={{ maxWidth: '600px' }}
              />
              <Typography variant="caption">
                <FormattedMessage
                  id="property-details-market-grade-note"
                  defaultMessage="Data provided by {airdnaLink}. {marketGradesLink}"
                  values={{
                    airdnaLink: (
                      <Link variant="inherit" href="https://www.airdna.co" target="_blank">
                        AirDNA
                      </Link>
                    ),
                    marketGradesLink: (
                      <Link
                        variant="inherit"
                        href={`${CONFIG.helpUrl}articles/6529515-how-is-market-grade-calculated-for-vacation-rentals`}
                        target="_blank"
                        underline="always"
                      >
                        Learn more about market grades
                      </Link>
                    ),
                  }}
                />
              </Typography>
            </Box>
          </Grid>
        )}
      </Grid>
    </PropertyDetailsSection>
  );
};

export const PropertyDetailsMarketInfo = ({ market, property, offering }: PropertyDetailsMarketInfoProps) => {
  if (offering.assetType === AssetType.LTR) {
    return <PropertyDetailsMarketInfoLTR market={market} property={property} offering={offering} />;
  } else {
    return <PropertyDetailsMarketInfoSTR market={market} property={property} offering={offering} />;
  }
};
